// src/components/Login.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  Car,
  Globe,
  ShieldCheck,
  AlertCircle,
  Loader2
} from "lucide-react";
import "./auth.css";

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [capsLockActive, setCapsLockActive] = useState(false);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handlePasswordKey = (e) => {
    if (e.getModifierState) {
      setCapsLockActive(e.getModifierState("CapsLock"));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await login(form.email.trim(), form.password);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      const msg = err?.response?.data?.message || "Invalid email or password";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-v2-page">
      {/* Left Panel: Executive Brand Showcase */}
      <aside className="login-v2-brand" aria-label="Brand Overview">
        <div className="login-v2-brand-glow" aria-hidden="true" />
        <div className="login-v2-brand-inner">
          <div className="login-v2-logo-wrapper">
            <img
              src="/logo.png"
              alt="Platinum Auto Japan"
              className="login-v2-brand-logo"
              onError={(e) => {
                e.target.onerror = null;
                e.target.outerHTML = '<div class="login-v2-logo-fallback">PAJ</div>';
              }}
            />
          </div>

          <h1 className="login-v2-brand-heading">Platinum Auto Japan</h1>
          <p className="login-v2-brand-tagline">
            Global vehicle export operations, one workspace.
          </p>

          <div className="login-v2-features">
            <div className="login-v2-feature-row">
              <span className="login-v2-feature-icon" aria-hidden="true">
                <Car size={18} />
              </span>
              <span className="login-v2-feature-text">Live stock and auctions</span>
            </div>

            <div className="login-v2-feature-row">
              <span className="login-v2-feature-icon" aria-hidden="true">
                <Globe size={18} />
              </span>
              <span className="login-v2-feature-text">Worldwide shipments</span>
            </div>

            <div className="login-v2-feature-row">
              <span className="login-v2-feature-icon" aria-hidden="true">
                <ShieldCheck size={18} />
              </span>
              <span className="login-v2-feature-text">Secure team access</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Right Panel: Sign-In Console */}
      <main className="login-v2-panel" aria-label="Authentication Form">
        <div className="login-v2-card">
          {/* Mobile-only compact logo */}
          <div className="login-v2-mobile-brand" aria-hidden="true">
            <img
              src="/logo.png"
              alt=""
              className="login-v2-mobile-logo"
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = "none";
              }}
            />
            <span className="login-v2-mobile-title">Platinum Auto Japan</span>
          </div>

          <div className="login-v2-header">
            <h2 className="login-v2-heading">Welcome back</h2>
            <p className="login-v2-subheading">Sign in to your CRM workspace</p>
          </div>

          {error && (
            <div className="login-v2-alert" role="alert" aria-live="assertive">
              <AlertCircle size={16} className="login-v2-alert-icon" aria-hidden="true" />
              <span className="login-v2-alert-text">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-v2-form" noValidate={false}>
            {/* Field: Corporate Email */}
            <div className="login-v2-field-group">
              <label htmlFor="login-email" className="login-v2-label">
                Corporate Email
              </label>
              <div className="login-v2-input-wrap">
                <Mail size={18} className="login-v2-field-icon" aria-hidden="true" />
                <input
                  id="login-email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  placeholder="name@platinumautojapan.com"
                  className="login-v2-input"
                  autoComplete="username"
                  autoFocus
                />
              </div>
            </div>

            {/* Field: Password */}
            <div className="login-v2-field-group">
              <label htmlFor="login-password" className="login-v2-label">
                Password
              </label>
              <div className="login-v2-input-wrap">
                <Lock size={18} className="login-v2-field-icon" aria-hidden="true" />
                <input
                  id="login-password"
                  name="password"
                  type={showPass ? "text" : "password"}
                  value={form.password}
                  onChange={handleChange}
                  onKeyDown={handlePasswordKey}
                  onKeyUp={handlePasswordKey}
                  required
                  placeholder="Enter your password"
                  className="login-v2-input"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="login-v2-eye-btn"
                  onClick={() => setShowPass((v) => !v)}
                  aria-label={showPass ? "Hide password" : "Show password"}
                  tabIndex={0}
                >
                  {showPass ? (
                    <EyeOff size={18} aria-hidden="true" />
                  ) : (
                    <Eye size={18} aria-hidden="true" />
                  )}
                </button>
              </div>

              {capsLockActive && (
                <div className="login-v2-capslock" role="status">
                  <AlertCircle size={13} aria-hidden="true" />
                  <span>Caps Lock is on</span>
                </div>
              )}
            </div>

            {/* Primary Action Button */}
            <button
              type="submit"
              className="login-v2-submit-btn"
              disabled={loading}
            >
              {loading ? (
                <span className="login-v2-btn-inner">
                  <Loader2 size={18} className="login-v2-spinner" aria-hidden="true" />
                  <span>Signing in...</span>
                </span>
              ) : (
                <span className="login-v2-btn-inner">
                  <span>Sign in</span>
                  <ArrowRight size={18} className="login-v2-btn-arrow" aria-hidden="true" />
                </span>
              )}
            </button>
          </form>

          {/* Footer Information */}
          <footer className="login-v2-footer">
            <p className="login-v2-footer-line">
              Authorized personnel only. Sessions are encrypted and monitored.
            </p>
            <p className="login-v2-footer-line login-v2-footer-meta">
              <span>Copyright &copy; 2026 Platinum Auto Japan</span>
              <span className="login-v2-support-wrap">
                <span className="login-v2-sep" aria-hidden="true">&middot;</span>
                <a
                  href="mailto:support@platinumautojapan.com"
                  className="login-v2-support-link"
                >
                  System Support
                </a>
              </span>
            </p>
          </footer>
        </div>
      </main>
    </div>
  );
};

export default Login;
